import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Group,
  Heading,
  Label,
  SelectMini,
  Tab,
  TabBar,
  Tabs,
  TabView,
} from '@salecycle/ui-component-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortNumericUpAlt } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { usePreferencesState } from 'core/preferences/usePreferencesContext';
import Spinner from 'core/ui/components/spinner';
import { ActivityState, Helpers } from 'entities/lifecycle';
import { ICampaign } from 'entities/campaign/campaign';
import { TFunction } from 'i18next';
import { connect } from 'react-redux';
import { Actions as RouterActions, Selectors as Router } from 'router';
import { Selectors as SiteSelectors } from 'sections/client-management/services/sites';
import { State } from 'store';
import { Selectors } from '../../state/campaigns';
import PageWrapper from '../components/PageWrapper';
import CampaignListItem from './components/CampaignListItem';
import CloneModal from './components/cloneModal';
import { campaignStateToTranslation } from '../../../utils/campaignStateToTranslation';
import { sortByAZ, sortByLast, sortByZA } from '../../../utils/sortBy';
import { AllActivityStates } from 'entities/lifecycle/activityState';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

type ICampaignTabId = ActivityState | 'All';

export interface IStateProps {
  showModal: boolean;
  siteId: string;
  priorities: string[];
  campaigns: ICampaign[];
  refreshing: boolean;
}

interface IDispatchProps {
  push: (href: string) => void;
}

type IProps = IStateProps & IDispatchProps;

const HeadingWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 3rem;
`;

const StyledCampaignsList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin-bottom: 10rem;
`;

const sortByOptions = [
  {
    value: 'LAST_MODIFIED',
    label: 'Last Modified',
  },
  {
    value: 'NAME_AZ',
    label: 'Name (A-Z)',
  },
  {
    value: 'NAME_ZA',
    label: 'Name (Z-A)',
  },
];

const sortCampaigns = (a: ICampaign, b: ICampaign, sortBy) => {
  switch (sortBy.value) {
    case 'LAST_MODIFIED':
      return sortByLast(a.audit.lastUpdatedAt, b.audit.lastUpdatedAt);
    case 'NAME_AZ':
      return sortByAZ(a.name, b.name);
    case 'NAME_ZA':
      return sortByZA(a.name, b.name);
  }
};

const tabIdToTranslation = (key: ICampaignTabId, t: TFunction) => {
  switch (key) {
    case 'All':
      return t('campaigns:allTab');
    default:
      return campaignStateToTranslation(key as ActivityState, t);
  }
};

const filterCampaignsByTabId = (campaigns: ICampaign[], tabId: ICampaignTabId) => {
  if (tabId === 'All') {
    return campaigns;
  }
  return campaigns.filter(campaign => Helpers.getActivityState(campaign) === tabId);
};

export const CampaignsList: FC<IProps> = props => {
  const { t } = useTranslation('campaigns');
  const { timezone } = usePreferencesState();
  const tabIds = ['All'].concat(AllActivityStates);
  const [activeTabId, setActiveTabId] = useState<ICampaignTabId>('All');
  const [sortBy, setSortBy] = useState(sortByOptions[0]);

  const newCampaign = () => props.push(`/sites/${props.siteId}/campaigns/campaigns/newcampaign/objective`);
  return (
    <>
      <PageWrapper>
        <Box stackItems={true}>
          <HeadingWrapper>
            <Heading>{t('campaigns:mps.heading')}</Heading>

            <Group>
              <Button variation="primary" onClick={newCampaign}>
                {t('campaigns:newCampaign')}
              </Button>
            </Group>
          </HeadingWrapper>

          {props.campaigns.length > 0 && !props.refreshing ? (
            <Tabs fillHeight={true}>
              <TabBar>
                {tabIds.map((id: ICampaignTabId) => (
                  <Tab
                    tabId={id}
                    key={id}
                    badgeLabel={String(filterCampaignsByTabId(props.campaigns, id).length)}
                    onClick={() => {
                      setActiveTabId(id);
                    }}
                  >
                    {tabIdToTranslation(id, t)}
                  </Tab>
                ))}
                <Box>
                  <Box margin="0 2rem 0 0">
                    <Label name={`${t('campaigns:sortBy')}:`} marginRight={true} />
                    <SelectMini
                      ariaLabel={t('campaigns:sortBy')}
                      value={sortBy}
                      options={sortByOptions}
                      onChange={selectedOption => setSortBy(selectedOption)}
                      menuPlacement="right"
                    />
                  </Box>
                  <Link to={`/sites/${props.siteId}/campaigns/priorities`}>
                    <FontAwesomeIcon icon={faSortNumericUpAlt as IconProp} />
                  </Link>
                </Box>
              </TabBar>

              <TabView tabId={activeTabId} padding={0}>
                <StyledCampaignsList>
                  {filterCampaignsByTabId(
                    props.campaigns.sort((a, b) => sortCampaigns(a, b, sortBy)),
                    activeTabId,
                  ).map(campaign => (
                    <CampaignListItem
                      campaign={campaign}
                      priorities={props.priorities}
                      siteId={props.siteId}
                      key={campaign.id}
                      timezone={timezone}
                    />
                  ))}
                </StyledCampaignsList>
              </TabView>
            </Tabs>
          ) : props.refreshing ? (
            <Spinner />
          ) : (
            <Box stackItems={true} horizontalPositioning="center" padding="40px 0">
              <h3>{t('campaigns:noCampaigns')}</h3>
            </Box>
          )}
        </Box>
      </PageWrapper>

      {props.showModal && <CloneModal />}
    </>
  );
};

function mapState(state: State): IStateProps {
  const siteId = Router.getSiteId(state);
  const site = SiteSelectors.getEntity(state, siteId);
  const priorities = (site && site.campaignPriorities) || [];
  const campaigns = Selectors.getEntityIds(state).map(id => ({ id, ...Selectors.getEntity(state, id) }));

  return {
    refreshing: Selectors.isRefreshing(state),
    siteId,
    priorities,
    campaigns,
    showModal: Router.getQuery(state, 'clone') === 'true' && !!Router.getQuery(state, 'campaign_id'),
  };
}

const actionCreators: IDispatchProps = {
  push: RouterActions.push,
};

export default connect(mapState, actionCreators)(CampaignsList);
